import {
  Control,
  useController,
  useWatch,
  useFormContext,
} from 'react-hook-form';
import { useMemo } from 'react';
import {
  createRequiredValidator,
  createMaxLengthValidator,
  createWebsiteValidator,
  isValidInput,
} from '../../Campaigns/validators';
import {
  createPatternValidator,
  createEinValidator,
} from '../../Brands/validators';
import { CspRegistrationFormData } from '../types';
import { ValidationRule } from '@/applications/CspPortal/Brands/types';
import regexExpressions from '@/constants/regexExpressions';

const ZIP_CODE_REGEX = regexExpressions.zip;
const ALPHA_NUMERIC_REGEX = new RegExp(regexExpressions.alphaNumeric);

export const useCspDetailsControllers = (
  control: Control<CspRegistrationFormData>,
  validationRules: ValidationRule[] = []
) => {
  const { setValue, clearErrors } = useFormContext<CspRegistrationFormData>();
  const entityType = useWatch({ control, name: 'details.entityType' });
  const country = useWatch({ control, name: 'details.country' });
  const market = useWatch({ control, name: 'register.market' });

  const isPublicCompany = entityType === 'PUBLIC_PROFIT';
  const isUS = country === 'US';
  const isDeMarket = market === 'DE';

  // Derived label for EIN/Tax Number field
  const einLabel = isDeMarket
    ? 'Company Registration Number / ID'
    : isUS
    ? 'EIN'
    : country
    ? 'Tax Number / ID'
    : 'Tax Number / ID / EIN';

  const postalCodeLabel = isUS
    ? 'Zip Code'
    : country
    ? 'Postal Code'
    : 'Postal Code / Zip Code';

  const einValidationRule = useMemo(
    () => validationRules?.find((item) => item.attributeName === 'ein'),
    [validationRules]
  );

  // CSP Legal Name (companyName)
  const {
    field: {
      value: companyName,
      onChange: setCompanyName,
      onBlur: validateCompanyName,
    },
    fieldState: { error: companyNameError },
  } = useController({
    control,
    name: 'details.companyName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter CSP Legal Name'),
        withValidMaxLength: createMaxLengthValidator(255),
        isValidPattern: createPatternValidator(
          ALPHA_NUMERIC_REGEX,
          'Invalid input'
        ),
      },
    },
  });

  // CSP DBA Name (displayName)
  const {
    field: {
      value: displayName,
      onChange: setDisplayName,
      onBlur: validateDisplayName,
    },
    fieldState: { error: displayNameError },
  } = useController({
    control,
    name: 'details.displayName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter CSP DBA Name'),
        withValidMaxLength: createMaxLengthValidator(255),
        isValidPattern: createPatternValidator(
          ALPHA_NUMERIC_REGEX,
          'Invalid input'
        ),
      },
    },
  });

  // Country of Registration (country)
  const {
    field: { value: countryOfRegistration, onChange: setCountryOfRegistration },
    fieldState: { error: countryError },
  } = useController({
    control,
    name: 'details.country',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select country'),
      },
    },
  });

  const handleCountryChange = (val: string) => {
    setCountryOfRegistration(val);
    // Reset dependent fields to prevent cross-component state residue
    setValue('details.street', '');
    setValue('details.city', '');
    setValue('details.state', '');
    setValue('details.postalCode', '');
    setValue('details.ein', '');
    // Clear previous errors for these fields
    clearErrors([
      'details.street',
      'details.city',
      'details.state',
      'details.postalCode',
      'details.ein',
    ]);
  };

  // Entity Type (entityType)
  const {
    field: { value: entityTypeVal, onChange: setEntityType },
    fieldState: { error: entityTypeError },
  } = useController({
    control,
    name: 'details.entityType',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select your entity type'),
      },
    },
  });

  // Tax ID/EIN (ein)
  const {
    field: { value: ein, onChange: setEin, onBlur: validateEin },
    fieldState: { error: einError },
  } = useController({
    control,
    name: 'details.ein',
    rules: {
      validate: {
        isRequired: createRequiredValidator(`Please enter ${einLabel}`),
        ...(isUS
          ? { isValidEin: createEinValidator(einValidationRule) }
          : { withValidMaxLength: createMaxLengthValidator(21) }),
      },
    },
  });

  // Address/Street (street)
  const {
    field: { value: street, onChange: setStreet, onBlur: validateStreet },
    fieldState: { error: streetError },
  } = useController({
    control,
    name: 'details.street',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Address/Street'),
        withValidMaxLength: createMaxLengthValidator(255),
        isValidPattern: createPatternValidator(
          ALPHA_NUMERIC_REGEX,
          'Invalid input'
        ),
      },
    },
  });

  // City (city)
  const {
    field: { value: city, onChange: setCity, onBlur: validateCity },
    fieldState: { error: cityError },
  } = useController({
    control,
    name: 'details.city',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter City'),
        withValidMaxLength: createMaxLengthValidator(100),
        isValidPattern: createPatternValidator(
          ALPHA_NUMERIC_REGEX,
          'Invalid input'
        ),
      },
    },
  });

  // State / Region (state)
  const {
    field: { value: state, onChange: setState, onBlur: validateState },
    fieldState: { error: stateError },
  } = useController({
    control,
    name: 'details.state',
    rules: {
      validate: {
        isValidInput,
        isRequired: (val) => {
          if (isUS) return !!val || 'Please select State';
          return !!val || 'Please enter State / Region';
        },
        withValidMaxLength: createMaxLengthValidator(50),
        isValidPattern: createPatternValidator(
          ALPHA_NUMERIC_REGEX,
          'Invalid input'
        ),
      },
    },
  });

  // Postal Code (postalCode)
  const {
    field: {
      value: postalCode,
      onChange: setPostalCode,
      onBlur: validatePostalCode,
    },
    fieldState: { error: postalCodeError },
  } = useController({
    control,
    name: 'details.postalCode',
    rules: {
      validate: {
        isRequired: (val) => !!val || `Please enter ${postalCodeLabel}`,
        maxLength: (val: string) =>
          val.length <= 20 || `Maximum 20 chars allowed for ${postalCodeLabel}`,
        isValidPostalCode: (value: string) => {
          if (isUS && value && !ZIP_CODE_REGEX.test(value)) {
            return 'Invalid ZIP code';
          }
          return isValidInput(value);
        },
      },
    },
  });

  // Website (website)
  const {
    field: { value: website, onChange: setWebsite, onBlur: validateWebsite },
    fieldState: { error: websiteError },
  } = useController({
    control,
    name: 'details.website',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter website'),
        withValidMaxLength: createMaxLengthValidator(255),
        isUrl: createWebsiteValidator(
          'Please enter a valid website URL with https:// protocol'
        ),
      },
    },
  });

  // Stock Symbol (stockSymbol)
  const {
    field: {
      value: stockSymbol,
      onChange: setStockSymbol,
      onBlur: validateStockSymbol,
      disabled: stockSymbolDisabled,
    },
    fieldState: { error: stockSymbolError },
  } = useController({
    control,
    name: 'details.stockSymbol',
    disabled: !isPublicCompany,
    rules: {
      validate: {
        isValidInput: (val: string) =>
          isValidInput(val) || 'Invalid Stock Symbol',
        isRequired: createRequiredValidator('Please enter Stock Symbol'),
        withValidMaxLength: createMaxLengthValidator(10),
      },
    },
  });

  // Stock Exchange (stockExchange)
  const {
    field: {
      value: stockExchange,
      onChange: setStockExchange,
      disabled: stockExchangeDisabled,
    },
    fieldState: { error: stockExchangeError },
  } = useController({
    control,
    name: 'details.stockExchange',
    disabled: !isPublicCompany,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Stock Exchange'),
      },
    },
  });

  // Snowshoe Question (questionnaireSnowshoeAnswer)
  const {
    field: {
      value: snowshoeAnswer,
      onChange: setSnowshoeAnswer,
      onBlur: validateSnowshoeAnswer,
    },
    fieldState: { error: snowshoeAnswerError },
  } = useController({
    control,
    name: 'details.questionnaireSnowshoeAnswer',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please answer this question'),
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  // ATO Question (questionnaireATOAnswer)
  const {
    field: {
      value: atoAnswer,
      onChange: setAtoAnswer,
      onBlur: validateAtoAnswer,
    },
    fieldState: { error: atoAnswerError },
  } = useController({
    control,
    name: 'details.questionnaireATOAnswer',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please answer this question'),
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  return {
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameError,
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    countryOfRegistration,
    setCountryOfRegistration: handleCountryChange,
    countryError,
    entityTypeVal,
    setEntityType,
    entityTypeError,
    ein,
    setEin,
    validateEin,
    einError,
    street,
    setStreet,
    validateStreet,
    streetError,
    city,
    setCity,
    validateCity,
    cityError,
    state,
    setState,
    validateState,
    stateError,
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeError,
    website,
    setWebsite,
    validateWebsite,
    websiteError,
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolError,
    stockSymbolDisabled,
    stockExchange,
    setStockExchange,
    stockExchangeError,
    stockExchangeDisabled,
    snowshoeAnswer,
    setSnowshoeAnswer,
    validateSnowshoeAnswer,
    snowshoeAnswerError,
    atoAnswer,
    setAtoAnswer,
    validateAtoAnswer,
    atoAnswerError,
    isPublicCompany,
    isUS,
    einLabel,
    postalCodeLabel,
  };
};

export default useCspDetailsControllers;
